import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  styled,
  IconButton,
  LinearProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDropzone } from 'react-dropzone';
import { assetPath } from '../../utils/constants';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '12px',
    maxWidth: '800px',
    width: '100%',
  },
  '& .MuiDialogContent-root': {
    padding: '32px',
  },
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: '12px',
  top: '12px',
  color: '#637381',
  backgroundColor: '#F5F6F7',
  '&:hover': {
    backgroundColor: '#E5E8EB',
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 600,
  color: '#212B36',
  textAlign: 'center',
  marginBottom: '16px',
}));

const Description = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  color: '#637381',
  textAlign: 'center',
  marginBottom: '32px',
}));

const DocumentTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginBottom: '24px',
  gap: '16px',
}));

const DocumentTab = styled(Button)(({ theme, active }) => ({
  backgroundColor: active ? '#EFF4FA' : '#F5F6F7',
  color: '#212B36',
  fontWeight: 500,
  padding: '16px 32px',
  borderRadius: '8px',
  textTransform: 'none',
  flexGrow: 1,
  '&:hover': {
    backgroundColor: active ? '#E5EEF5' : '#EAECEE',
  },
}));

const DropzoneContainer = styled(Box)(({ theme }) => ({
  border: '1px dashed #919EAB',
  borderRadius: '8px',
  padding: '48px 24px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#F9FAFB',
  cursor: 'pointer',
  marginBottom: '16px',
}));

const UploadIcon = styled('img')(({ theme }) => ({
  width: '48px',
  height: '48px',
  marginBottom: '16px',
}));

const FileTypeText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#637381',
  marginTop: '8px',
}));

const UploadButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0052CC',
  color: 'white',
  textTransform: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: '#003E99',
  },
}));

const UploadedFilesSection = styled(Box)(({ theme }) => ({
  marginTop: '24px',
}));

const UploadedFileTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: '#637381',
  marginBottom: '16px',
}));

const FileItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  border: '1px solid #E5E8EB',
  borderRadius: '8px',
  marginBottom: '16px',
}));

const FileIcon = styled('img')(({ theme }) => ({
  width: '32px',
  height: '32px',
  marginRight: '16px',
}));

const FileInfo = styled(Box)(({ theme }) => ({
  flex: 1,
}));

const FileName = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: '#212B36',
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#FF4842',
  marginTop: '4px',
}));

const ProgressContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  marginRight: '16px',
}));

const StyledProgressBar = styled(LinearProgress)(({ theme, error }) => ({
  height: '8px',
  borderRadius: '4px',
  backgroundColor: error ? '#FFE9E9' : '#E5E8EB',
  '& .MuiLinearProgress-bar': {
    backgroundColor: error ? '#FF4842' : '#00AB55',
  },
}));

const UploadPercentage = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#212B36',
  marginTop: '4px',
}));

const ActionIcon = styled(IconButton)(({ theme, success, error }) => ({
  color: success ? '#00AB55' : error ? '#FF4842' : '#637381',
  backgroundColor: '#F5F6F7',
  marginLeft: '8px',
  '&:hover': {
    backgroundColor: '#E5E8EB',
  },
}));

const MainButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#0052CC',
  color: 'white',
  textTransform: 'none',
  borderRadius: '8px',
  padding: '12px 0',
  fontWeight: 600,
  marginTop: '24px',
  width: '100%',
  '&:hover': {
    backgroundColor: '#003E99',
  },
}));

const DocumentReuploadModal = ({ open, onClose }) => {
  const [selectedTab, setSelectedTab] = useState('aadhar');
  const [uploadedFiles, setUploadedFiles] = useState([
    {
      id: 1,
      name: 'Aadhar card.pdf',
      progress: 100,
      status: 'success',
      error: null,
    },
    {
      id: 2,
      name: 'Bank Statement.pdf',
      progress: 40,
      status: 'error',
      error: 'Error Uploading',
    },
  ]);

  const onDrop = useCallback((acceptedFiles) => {    
    const newFile = {
      id: Date.now(),
      name: acceptedFiles[0].name,
      progress: 0,
      status: 'uploading',
      error: null,
    };
    
    setUploadedFiles([...uploadedFiles, newFile]);
    
    const timer = setInterval(() => {
      setUploadedFiles(prevFiles => 
        prevFiles.map(file => 
          file.id === newFile.id 
            ? { 
                ...file, 
                progress: file.progress + 10,
                status: file.progress + 10 >= 100 ? 'success' : 'uploading'
              } 
            : file
        )
      );
      
      if (newFile.progress >= 100) {
        clearInterval(timer);
      }
    }, 500);
  }, [uploadedFiles]);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleDeleteFile = (id) => {
    setUploadedFiles(uploadedFiles.filter(file => file.id !== id));
  };

  const handleRetryUpload = (id) => {
    setUploadedFiles(uploadedFiles.map(file => 
      file.id === id 
        ? { ...file, progress: 0, status: 'uploading', error: null } 
        : file
    ));
    
    const timer = setInterval(() => {
      setUploadedFiles(prevFiles => 
        prevFiles.map(file => 
          file.id === id 
            ? { 
                ...file, 
                progress: file.progress + 10,
                status: file.progress + 10 >= 100 ? 'success' : 'uploading'
              } 
            : file
        )
      );
      
      const updatedFile = uploadedFiles.find(file => file.id === id);
      if (updatedFile && updatedFile.progress >= 100) {
        clearInterval(timer);
      }
    }, 500);
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <CloseButton onClick={onClose} size="small">
          <CloseIcon />
        </CloseButton>
        
        <Title>Re-upload Required Documents</Title>
        <Description>
          The bank has requested you to re-upload the following documents to proceed
          with your loan application. Ensure the files are clear and in the correct format.
        </Description>

        <DocumentTabs>
          <DocumentTab 
            active={selectedTab === 'aadhar'} 
            onClick={() => setSelectedTab('aadhar')}
          >
            Aadhar Card
          </DocumentTab>
          <DocumentTab 
            active={selectedTab === 'bank'} 
            onClick={() => setSelectedTab('bank')}
          >
            Bank Statement
          </DocumentTab>
        </DocumentTabs>

        <Box {...getRootProps()}>
          <input {...getInputProps()} />
          <DropzoneContainer>
            <UploadIcon
              src={assetPath('uploadImage.png')}
              alt="Upload"
            />
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              Drag and drop your image to upload
            </Typography>
            <FileTypeText>
              Supported formats: PDF, JPG, PNG, JPEG
            </FileTypeText>
            <FileTypeText sx={{ marginTop: '4px' }}>
              (Max size: 5MB)
            </FileTypeText>
            <UploadButton variant="contained" sx={{ marginTop: '16px' }}>
              Select File
            </UploadButton>
          </DropzoneContainer>
        </Box>

        {uploadedFiles.length > 0 && (
          <UploadedFilesSection>
            <UploadedFileTitle>Upload Multiple Documents</UploadedFileTitle>
            
            {uploadedFiles.map((file) => (
              <FileItem key={file.id}>
                <FileIcon
                  src={assetPath('PDF_File_Type.png')}
                  alt="PDF"
                />
                <FileInfo>
                  <FileName>{file.name}</FileName>
                  {file.error && <ErrorText>{file.error}</ErrorText>}
                </FileInfo>
                
                <ProgressContainer>
                  <StyledProgressBar 
                    variant="determinate" 
                    value={file.progress} 
                    error={file.status === 'error'}
                  />
                  <UploadPercentage>{file.progress}%</UploadPercentage>
                </ProgressContainer>
                
                {file.status === 'success' && (
                  <ActionIcon success size="small">
                    <CheckCircleIcon />
                  </ActionIcon>
                )}
                
                {file.status === 'error' && (
                  <ActionIcon error size="small" onClick={() => handleRetryUpload(file.id)}>
                    <RestartAltIcon />
                  </ActionIcon>
                )}
                
                <ActionIcon size="small" onClick={() => handleDeleteFile(file.id)}>
                  <DeleteIcon />
                </ActionIcon>
              </FileItem>
            ))}
          </UploadedFilesSection>
        )}

        <MainButton variant="contained">
          Upload Documents
        </MainButton>
      </DialogContent>
    </StyledDialog>
  );
};

export default DocumentReuploadModal; 